import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";
import {
  isValidAgeRange,
  isValidFinnishBusinessNumber,
  isValidFinnishEmailAddress,
  isValidFinnishPersonalNumber,
  isValidFinnishPhoneNumber,
  isValidFinnishPostalCode,
  isValidGridAreaId,
  isValidPassword,
  isValidSwedishBusinessNumber,
  isValidSwedishEmailAddress,
  isValidSwedishPersonalNumber,
  isValidSwedishPhoneNumber,
  isValidSwedishPostalCode,
  isValidSwedishPremiseId,
  isValidStreetName,
  isValidCareOfAddress,
  isValidSwedishFinnishPostalTown,
} from "@vattenfall/util";

import { Region } from "../elements/elements";

/**
 * Check if value pass to formControl is empty or null.
 */
function isEmptyInputValue(value: unknown): boolean {
  return value == null || (typeof value === "string" && value.length === 0);
}

/**
 * Provides a set of custom built Vattenfall Elements validators that can be used by form controls.
 *
 * A validator is a function that processes a `FormControl` or collection of
 * controls and returns an error map or null. A null map means that validation has passed.
 *
 */
export class ElementsValidators {
  /**
   * Validator that requires the control's value pass a email address and tests that it is properly formated and has an offical domain extention.
   *
   * @param region specifies region (`se`) or (`fi`) for which validation is supported
   *
   * ### Validate that the field matches a valid email address pattern
   *
   * ```typescript
   * const control = new FormControl('firstname.lastname@gmail.com', ElementsValidators.emailAddress("se"));
   *
   * const control = new FormControl('förstname.lästname@gmail.com', ElementsValidators.emailAddress("fi"));
   *
   * console.log(control.errors); // { emailAddress: true } or throw error on non-supported region
   * ```
   *
   * @returns An error map with the `emailAddress` property
   * if the validation check fails, otherwise `null`.
   */
  static emailAddress(region: Region): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (isEmptyInputValue(control.value)) return null;
      switch (region) {
        case "fi":
          return isValidFinnishEmailAddress(control.value) ? null : { emailAddress: true };
        case "se":
          return isValidSwedishEmailAddress(control.value) ? null : { emailAddress: true };
        default:
          throw Error(
            `[ Vattenfall Elements ] The provided region '${region}' is not supported by the validator 'emailAddress'.`
          );
      }
    };
  }

  /**
   * Validator that requires the control's value pass an swedish organisation number/finland business-id regex test and algorithm.
   *
   * @param region specifies region (`fi`) or (`se`) for which validation is supported
   *
   * ### Validate that the field matches a valid organisationNumber pattern
   *
   * ```typescript
   * const control = new FormControl('556036-2138', ElementsValidators.organisationNumber("se"));
   *
   * console.log(control.errors); // { organisationNumber: true } or throw error on non-supported region
   * ```
   *
   * @returns An error map with the `organisationNumber` property
   * if the validation check fails, otherwise `null`.
   */
  static organisationNumber(region: Region): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (isEmptyInputValue(control.value)) return null;
      switch (region) {
        case "fi":
          return isValidFinnishBusinessNumber(control.value) ? null : { organisationNumber: true };
        case "se":
          return isValidSwedishBusinessNumber(control.value) ? null : { organisationNumber: true };
        default:
          throw Error(
            `[ Vattenfall Elements ] The provided region '${region}' is not supported by the validator 'organisationNumber'.`
          );
      }
    };
  }

  /**
   * Validator that requires the control's value pass an swedish/finnish personal number regex test and algorithm.
   *
   * @param region specifies region (`fi`)and (`se`) for which validation pattern is supported
   * @param minAge specifies legal minimum age to validate social security number
   * @param maxAge specifies legal maximum age to validate social security number
   *
   * ### Validate that the field matches a valid personal number pattern and algorithm
   *
   * ```typescript
   * const control = new FormControl('220556-029L', ElementsValidators.personalNumber("fi"));
   *
   * const control = new FormControl('640823-3234', ElementsValidators.personalNumber("se"));
   *
   * console.log(control.errors); // { personalNumber: true } or throw error on non-supported region
   * ```
   *
   * @returns An error map with the `personalNumber` property
   * if the validation check fails, otherwise `null`.
   */
  static personalNumber(region: Region, minAge?: number, maxAge?: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (isEmptyInputValue(control.value)) return null;
      switch (region) {
        case "fi":
          if (isValidFinnishPersonalNumber(control.value))
            if (isValidAgeRange(control.value, region, minAge, maxAge)) return null;
            else return { personalNumberAge: true };
          else return { personalNumber: true };
        case "se":
          if (isValidSwedishPersonalNumber(control.value))
            if (isValidAgeRange(control.value, region, minAge, maxAge)) return null;
            else return { personalNumberAge: true };
          else return { personalNumber: true };
        default:
          throw Error(
            `[ Vattenfall Elements ] The provided region '${region}' is not supported by the validator 'personalNumber'.`
          );
      }
    };
  }

  /**
   * Validator that requires the control's value pass an swedish/finnish phone number regex test and algorithm.
   *
   * @param region specifies region (`fi`)and (`se`) for which validation pattern is supported
   *
   * ### Validate that the field matches a valid phone number pattern and algorithm
   *
   * ```typescript
   * const control = new FormControl('+358406361091', ElementsValidators.phoneNumber("fi"));
   *
   * const control = new FormControl('+4620-820000', ElementsValidators.phoneNumber("se"));
   *
   * console.log(control.errors); // { phoneNumber: true } or throw error on non-supported region
   * ```
   *
   * @returns An error map with the `phoneNumber` property
   * if the validation check fails, otherwise `null`.
   */
  static phoneNumber(region: Region): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (isEmptyInputValue(control.value)) return null;
      switch (region) {
        case "fi":
          return isValidFinnishPhoneNumber(control.value) ? null : { phoneNumber: true };
        case "se":
          return isValidSwedishPhoneNumber(control.value) ? null : { phoneNumber: true };
        default:
          throw Error(`[ Vattenfall Elements ] The provided region '${region}' is not supported by the validator 'phoneNumber'.`);
      }
    };
  }

  /**
   * Validator that requires the control's value pass an swedish premise id validation test.
   *
   * @param region specifies region (`se`) for which validation pattern is supported
   *
   * ### Validate that the field matches a valid premise id pattern
   *
   * ```typescript
   * const control = new FormControl('735999100054384456', ElementsValidators.premiseId("se"));
   *
   * console.log(control.errors); // { premiseId: true } or throw error on non-supported region
   * ```
   *
   * @returns An error map with the `premiseId` property
   * if the validation check fails, otherwise `null`.
   */
  static premiseId(region: Region): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (isEmptyInputValue(control.value)) return null;
      switch (region) {
        case "se":
          return isValidSwedishPremiseId(control.value) ? null : { premiseId: true };
        default:
          throw Error(`[ Vattenfall Elements ] The provided region '${region}' is not supported by the validator 'premiseId'.`);
      }
    };
  }

  /**
   * Validator that requires the control's value pass an swedish/finnish postal code regex test and algorithm.
   *
   * @param region specifies region (`fi`)and (`se`) for which validation pattern is supported
   *
   * ### Validate that the field matches a valid postal code pattern and algorithm
   *
   * ```typescript
   * const control = new FormControl('62100', ElementsValidators.postalCode("fi"));
   *
   * const control = new FormControl('16956', ElementsValidators.postalCode("se"));
   *
   * console.log(control.errors); // { postalCode: true } or throw error on non-supported region
   * ```
   *
   * @returns An error map with the `postalCode` property
   * if the validation check fails, otherwise `null`.
   *
   *
   */
  static postalCode(region: Region): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (isEmptyInputValue(control.value)) return null;
      switch (region) {
        case "fi":
          return isValidFinnishPostalCode(control.value) ? null : { postalCode: true };
        case "se":
          return isValidSwedishPostalCode(control.value) ? null : { postalCode: true };
        default:
          throw Error(`[ Vattenfall Elements ] The provided region '${region}' is not supported by the validator 'postalCode'.`);
      }
    };
  }

  /**
   * Validator that requires the control's value pass the regexp for passwordvalidation.
   *
   * ```typescript
   * const control = new FormControl('azAZ09!@#$%^&*', ElementsValidators.password())
   * console.log(control.errors); {password: true}
   * ```
   */
  static password(): ValidatorFn {
    // TODO (stefan): This needs to be locale sensitive
    return (control: AbstractControl): ValidationErrors | null => {
      if (isEmptyInputValue(control.value)) return null;
      return isValidPassword(control.value) ? null : { password: true };
    };
  }

  /**
   * Validator that requires the control's value pass an swedish grid area id.
   *
   * ```typescript
   * const control = new FormControl('STH', ElementsValidators.gridAreaId())
   * console.log(control.errors); {gridAreaId: true}
   * ```
   */
  static gridAreaId(): ValidatorFn {
    // TODO (stefan): This needs to be locale sensitive
    return (control: AbstractControl): ValidationErrors | null => {
      if (isEmptyInputValue(control.value)) return null;
      return isValidGridAreaId(control.value) ? null : { gridAreaId: true };
    };
  }

  /**
   * Validator that requires the control's value pass the regexp for street name validation.
   *
   * for premise, const control = new FormControl('a-zA-Z0-9åäöÅÄÖ', ElementsValidators.streetName())
   * for invoice, const control = new FormControl('a-zA-Z0-9åäöÅÄÖÆæØøÜüß', ElementsValidators.streetName())
   * console.log(control.errors); {streetName: true}
   * ```
   */
  static streetName(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (isEmptyInputValue(control.value)) return null;
      return isValidStreetName(control.value) ? null : { streetName: true };
    };
  }

  /**
   * Validator that requires the control's value pass the regexp for care of address.
   *
   * const control = new FormControl('azAZ099åäöÅÄÖÆæØøÜüß ElementsValidators.careOfAddress())
   * console.log(control.errors); {careOfAddress: true}
   */
  static careOfAddress(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      return isValidCareOfAddress(control.value) ? null : { careOfAddress: true };
    };
  }

  /**
   * Validator that requires the control's value pass the regexp for postal town validation.
   *
   * ```typescript
   * const control = new FormControl('Örebro', ElementsValidators.postalTown())
   *
   * console.log(control.errors); // { postalTown: true } or throw error on non-supported region
   * ```
   */
  static postalTown(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (isEmptyInputValue(control.value)) return null;
      return isValidSwedishFinnishPostalTown(control.value) ? null : { postalTown: true };
    };
  }
}
